// Onboarding, kept per account (Features 4, 26 Oct: "skippable and remembered per account"). One small record on the
// workspace (Workspace.onboarding), owner-only like planSeen and dismissed: it travels in the owner's snapshot and
// workspace events to every device of the account, never to a shared link, a coworker or an agent.
//
// Every field merges the same way on every device, so two devices never undo each other: a mark is a signed time
// (a positive time for on, a negative one for off, the later of the two wins), a map merges key by key, and a
// time only moves forward. The browser writes the whole record with each change (workspace.put), once per change,
// and the server merges what it holds with what came (mergeOnboarding).

import type { BoardKind, ID } from "./types";
import { KIND_ORDER } from "./boardKinds";

/** What a new account answered to "What do you want to use this for?" (shared/starters.ts makes each board). */
export const STARTER_IDS = ["business", "build", "content", "clients", "day"] as const;
export type StarterId = (typeof STARTER_IDS)[number];
export type Choice = StarterId | "skip";

/** The checklist's steps, in the order they show once the first is done (src/chrome/Checklist.tsx). */
export const STEP_IDS = ["agent", "mcps", "apis", "files", "computers"] as const;
export type StepId = (typeof STEP_IDS)[number];

/**
 * A switch with when it was set: a positive time is on, a negative one off, and the later wins. 0 or missing is
 * never set. Read it with isOn, make one with stamp.
 */
export type Mark = number;

export interface Onboarding {
  /** the answer at the first question and when; set once, when the account starts (WorkspaceDO.init), never changed */
  choice?: { id: Choice; at: number } | null;
  /** checklist steps the user ticked off themselves, by StepId; a step that is really connected ticks itself */
  ticked?: Partial<Record<StepId, Mark>>;
  /** the checklist put away for good (its X); on, it never shows again on any device */
  hidden?: Mark;
  /** when the user last saw the checklist open, for OB's tips to wait until it has had its turn */
  seen?: number;
  /** board kinds whose first-time tips were seen or skipped, with when (OB, part 4) */
  tips?: Partial<Record<BoardKind, number>>;
  /** every tip and hint off (Settings, OB) */
  tipsOff?: Mark;
  /** "did you know" hints shown once, by id ("arrange-board"), with when (OB, part 4) */
  hints?: Record<string, number>;
  /** where the user was: the board, the page on a doc or plan board, when, and which browser (OB, part 6: open where
   * you left off; a browser's own last place is the one it remembers itself, src/help/leftOff.ts) */
  last?: { board: ID; page?: ID | null; at: number; dev?: string } | null;
  /** when the user last looked at the board, for While you were away (OB, part 6); only moves forward */
  visit?: number;
  /** the What's new note last seen, by its id, and when (OB, part 6) */
  news?: { id: string; at: number } | null;
  /** when a board's theme was first changed (was cb:themed in this browser) */
  themed?: number;
  /** the start's suggested message, once it has been put in the chat, so a reload never types it again */
  prompted?: number;
}

/** The fields, in one place, so a new one is added to the cleaner as well (tests/onboarding.test.ts checks). */
export const ONBOARDING_FIELDS = ["choice", "ticked", "hidden", "seen", "tips", "tipsOff", "hints", "last", "visit", "news", "themed", "prompted"] as const;

/** Whether a mark is on. */
export const isOn = (m: Mark | null | undefined): boolean => typeof m === "number" && m > 0;

/** A mark for now, on or off. Never 0, which reads as never set. */
export const stamp = (on: boolean, now = Date.now()): Mark => (on ? 1 : -1) * Math.max(1, Math.round(now));

const MAX_HINTS = 60;
// every kind of board, so a new kind's tips are kept as seen too (a Custom board's were left out and came back each visit, 1 Nov)
const KINDS: BoardKind[] = KIND_ORDER;
const ID_RE = /^[A-Za-z0-9_-]{2,48}$/;

/** A time that can be true: a finite number, not from the future (a minute of clock drift allowed). */
function time(v: unknown, now: number): number | null {
  return typeof v === "number" && Number.isFinite(v) && v > 0 && v <= now + 60_000 ? Math.round(v) : null;
}
function mark(v: unknown, now: number): Mark | null {
  return typeof v === "number" && Number.isFinite(v) && v !== 0 && Math.abs(v) <= now + 60_000 ? Math.round(v) : null;
}
/** The later of two marks; at the same time, on wins. */
function laterMark(a: Mark | null, b: Mark | null): Mark | null {
  if (a === null) return b;
  if (b === null) return a;
  return Math.abs(b) > Math.abs(a) || (Math.abs(b) === Math.abs(a) && b > 0) ? b : a;
}
const obj = (v: unknown): Record<string, unknown> | null => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null);

/** A map of marks or times, cleaned and merged key by key: the later of each. */
function mergeMap(a: unknown, b: unknown, keyOk: (k: string) => boolean, read: (v: unknown) => number | null, later: (x: number | null, y: number | null) => number | null, max: number): Record<string, number> | undefined {
  const out: Record<string, number> = {};
  for (const src of [obj(a), obj(b)]) {
    if (!src) continue;
    for (const [k, v] of Object.entries(src)) {
      if (!keyOk(k)) continue;
      const got = later(out[k] ?? null, read(v));
      if (got !== null) out[k] = got;
    }
  }
  const keys = Object.keys(out);
  if (!keys.length) return undefined;
  // the newest kept, when there are more than fit
  return keys.length > max ? Object.fromEntries(Object.entries(out).sort((x, y) => Math.abs(y[1]) - Math.abs(x[1])).slice(0, max)) : out;
}
const laterTime = (x: number | null, y: number | null) => (x === null ? y : y === null ? x : Math.max(x, y));

function cleanChoice(v: unknown, now: number): Onboarding["choice"] | undefined {
  const o = obj(v);
  if (!o) return undefined;
  const id = o.id;
  const at = time(o.at, now);
  return at !== null && (id === "skip" || (STARTER_IDS as readonly unknown[]).includes(id)) ? { id: id as Choice, at } : undefined;
}
function cleanLast(v: unknown, now: number): Onboarding["last"] | undefined {
  const o = obj(v);
  if (!o) return undefined;
  const at = time(o.at, now);
  if (at === null || typeof o.board !== "string" || !ID_RE.test(o.board)) return undefined;
  const page = typeof o.page === "string" && ID_RE.test(o.page) ? o.page : null;
  const dev = typeof o.dev === "string" && /^[a-z0-9]{6,16}$/.test(o.dev) ? o.dev : null;
  return { board: o.board, page, at, ...(dev ? { dev } : {}) };
}
function cleanNews(v: unknown, now: number): Onboarding["news"] | undefined {
  const o = obj(v);
  if (!o) return undefined;
  const at = time(o.at, now);
  return at !== null && typeof o.id === "string" && /^[\w.-]{1,40}$/.test(o.id) ? { id: o.id, at } : undefined;
}
/** The one with the later time. */
const laterOf = <T extends { at: number }>(a: T | undefined, b: T | undefined): T | undefined => (!a ? b : !b ? a : b.at >= a.at ? b : a);

/**
 * What the account keeps: what it held merged with what a device sent. Anything that is not a field, or not a
 * value that field can hold, is left out; a device's record can never take back what another device set.
 */
export function mergeOnboarding(was: unknown, sent: unknown, now = Date.now()): Onboarding {
  const a = obj(was) ?? {};
  const b = obj(sent) ?? {};
  const out: Onboarding = {};
  // the first answer stands: the question is asked once
  const ca = cleanChoice(a.choice, now);
  const cb = cleanChoice(b.choice, now);
  const choice = !ca ? cb : !cb ? ca : cb.at < ca.at ? cb : ca;
  if (choice) out.choice = choice;
  const ticked = mergeMap(a.ticked, b.ticked, (k) => (STEP_IDS as readonly string[]).includes(k), (v) => mark(v, now), laterMark, STEP_IDS.length);
  if (ticked) out.ticked = ticked;
  for (const k of ["hidden", "tipsOff"] as const) {
    const m = laterMark(mark(a[k], now), mark(b[k], now));
    if (m !== null) out[k] = m;
  }
  for (const k of ["seen", "visit", "themed", "prompted"] as const) {
    const t = laterTime(time(a[k], now), time(b[k], now));
    if (t !== null) out[k] = t;
  }
  const tips = mergeMap(a.tips, b.tips, (k) => (KINDS as string[]).includes(k), (v) => time(v, now), laterTime, KINDS.length);
  if (tips) out.tips = tips;
  const hints = mergeMap(a.hints, b.hints, (k) => /^[a-z0-9-]{2,40}$/.test(k), (v) => time(v, now), laterTime, MAX_HINTS);
  if (hints) out.hints = hints;
  const last = laterOf(cleanLast(a.last, now) ?? undefined, cleanLast(b.last, now) ?? undefined);
  if (last) out.last = last;
  const news = laterOf(cleanNews(a.news, now) ?? undefined, cleanNews(b.news, now) ?? undefined);
  if (news) out.news = news;
  return out;
}

/** A record as one device holds it, cleaned on its own (what a snapshot brings, before anything is merged into it). */
export const cleanOnboarding = (v: unknown, now = Date.now()): Onboarding => mergeOnboarding(v, null, now);

/** Whether a step counts as done by the user's own tick. */
export const tickedOff = (o: Onboarding | null | undefined, id: StepId): boolean => isOn(o?.ticked?.[id]);
